import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthConfig, AuthService } from 'src/app/Services/auth.service';
import { DashboardService } from 'src/app/Services/dashboard.service';
import { GalleryDashboard } from 'src/app/models/response/galleryDashboard.models';
import { OnBoardingService } from 'src/app/Services/shared/onboarding.service';
import { ImageUtils } from 'src/app/shared/utils/image-utils';

@Component({
    selector: 'ds-gallery-dashboard',
    templateUrl: './ds-gallery-dashboard.component.html',
    styleUrls: ['./ds-gallery-dashboard.component.css'],
    providers: [DashboardService],
    standalone: false
})
export class DsGalleryDashboardComponent implements OnInit {
    public galleryDashboardData: GalleryDashboard = {
        ConnectedArtist: [],
        GalleryStockReports: [],
        ConsignmentApprovalPending: [],
        ProductRequestPending: [],
        IncomingGalleryConnectionRequest: []
    };
    public galleryName: string = '';
    public dashboardDataLoaded: boolean = false;
    public selectedTabIndex: number = 0;
    public isAuthenticated!: boolean;
    public isGallery: boolean = false;
    public showDashboard: boolean = true;
    public authConfig: AuthConfig = {};

    /** Hero tab on small screens: the cockpit ('attention') or the snapshot ('glance'). */
    public heroTab: 'attention' | 'glance' = 'attention';

    public get pendingAccept(): number { return this.galleryDashboardData.ConsignmentApprovalPending?.length || 0; }
    public get pendingRequests(): number { return this.galleryDashboardData.ProductRequestPending?.length || 0; }
    public get pendingConnections(): number { return this.galleryDashboardData.IncomingGalleryConnectionRequest?.length || 0; }
    public get attentionTotal(): number { return this.pendingAccept + this.pendingRequests + this.pendingConnections; }

    /** Connected-artist count from the dashboard aggregation API (TotalConnectedArtist).
     *  The old array-length fallback was dropped after the backend aggregation fix (2026-06-29). */
    public get connectedArtistCount(): number {
        return this.galleryDashboardData.TotalConnectedArtist || 0;
    }

    constructor(
        private ngxSpinner: NgxSpinnerService,
        private router: Router,
        private authService: AuthService,
        private dashboardService: DashboardService,
        private onBoardingService: OnBoardingService
    ) { }

    public ngOnInit(): void {
        this.getGalleryDashboard();
        const currentUser = this.authService.getCurrentUser();
        this.galleryName = currentUser?.FirstName || 'Gallery';
        this.isAuthenticated = this.authService.isAuthenticated();
        if (this.isAuthenticated) {
            this.authConfig = this.authService.getAuthConfig();
            this.isGallery = this.authConfig.IsGallery || false;
            this.onBoardingService.getUpdatedLocalStorageValue.subscribe((tourVisible: boolean) => {
                this.showDashboard = !tourVisible;
            });
        }
    }

    public getGalleryDashboard() {
        this.ngxSpinner.show();
        this.dashboardService.getGalleryDashboard().subscribe((data: GalleryDashboard) => {
            data.BestPerformingArtistData?.forEach((item) => {
                const colorClass = this.getColorBasedOnPercentage(item.PercentageOfTotal);
                item.ColorClassBasedOnPercentage = 'bg-' + colorClass;
                item.TextBasedOnPercentage = `<span class="text-${colorClass}">${item.PercentageOfTotal}%</span>`;
            });

            data.BestPerformingArtTypeData?.forEach((item) => {
                const colorClass = this.getColorBasedOnPercentage(item.PercentageOfTotal);
                item.ColorClassBasedOnPercentage = 'bg-' + colorClass;
                item.TextBasedOnPercentage = `<span class="text-${colorClass}">${item.PercentageOfTotal}%</span>`;
            });

            this.galleryDashboardData = data;
            if (data.NewProductsAdded) {
                this.galleryDashboardData.NewProductsAdded = data.NewProductsAdded.map((item) => {
                    item.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(item.Img || 'assets/images/No-Image.png');
                    return item;
                });
            }
            if (data.RecentSales) {
                this.galleryDashboardData.RecentSales = data.RecentSales.map((item) => {
                    item.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(item.Img || 'assets/images/No-Image.png');
                    return item;
                });
            }
            this.ngxSpinner.hide();
            this.dashboardDataLoaded = true;
        }, error => {
            console.error(error);
            this.ngxSpinner.hide();
        });
    }

    private getColorBasedOnPercentage(percentage: number): string {
        if (percentage >= 75) {
            return 'success';
        } else if (percentage >= 50) {
            return 'info';
        } else if (percentage >= 25) {
            return 'warning';
        }
        return 'danger';
    }

    public goToProductDetails(product: any) {
        this.router.navigate(['/', product.ArtistId, 'artworks', product.ArtistProductId]);
    }

    public onTabChange(event: any) {
        if (event.index !== undefined) {
            this.selectedTabIndex = event.index;
        }
    }

    public navigateToConnectedArtists() {
        this.router.navigate(['/galleries', 'connected-artists']);
    }

    public navigateToConsignmentProducts() {
        this.router.navigate(['/galleries', 'gallery-products']);
    }

    public navigateToCreateProduct() {
        this.router.navigate(['/galleries', 'gallery-product', 'create']);
    }

    public navigateToGalleryProducts() {
        this.router.navigate(['/galleries', 'view-gallery-products']);
    }

    public navigateToRequestedProducts() {
        this.router.navigate(['/galleries', 'requested-products']);
    }

    public navigateToEvents() {
        this.router.navigate(['/events']);
    }

    public navigateToPortfolio() {
        this.router.navigate(['/galleries', 'portfolio']);
    }

    public navigateToStaff() {
        this.router.navigate(['/gallery-staff']);
    }

    public navigateToConsignments() {
        this.router.navigate(['/view-consignments']);
    }

    public navigateToProposals() {
        this.router.navigate(['/galleries', 'view-proposals']);
    }

    getFirstSentence(message: string, wordCount: number = 10): string {
        if (!message) return '';
        const words = message.split(' ');
        if (words.length <= wordCount) {
            return message;
        }
        return words.slice(0, wordCount).join(' ') + '...';
    }
}
